import { useCallback, useMemo, useState } from 'react';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import {
  ColumnSort,
  createColumnHelper,
  filterFns,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Checkbox,
  Flex,
  LinkButton,
  Scrollbar,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { dataAttr } from '@tonic-one/utils';
import IconButton from '@Components/IconButton';
import {
  AggressivityFilter,
  CorrelatedRule,
  CorrelatedRuleShallow,
  RuleType,
  RuleTypeFilter,
  ThreatTypeFilter,
} from '../types';
import { aggressivityFilterI18nKeyMap, AllAggressivity } from '../utils';
import { useTranslation } from 'react-i18next';
import PreDefinedRuleModal from './PreDefinedRuleModal';
import CustomRuleModal from './CustomRuleModal';
import DeleteRuleModal from './DeleteRuleModal';
import { DeleteIcon, FileCopyOIcon } from '@tonic-one/react-icons';
import TagListOverflow from '@Quarantine/RiskAI/TagListOverflow';

const ROW_HEIGHT = 40;

const CorrelatedRulesTable = (props: {
  rules: CorrelatedRuleShallow[];
  nameFilter: string;
  ruleTypeFilter: RuleTypeFilter;
  aggressivityFilter: AggressivityFilter;
  threatTypeFilter: ThreatTypeFilter;
  onCopyRule: (rule: CorrelatedRule) => Promise<void>;
  onEditRule: (rule: CorrelatedRule) => Promise<void>;
  onDeleteRule: (ruleId: string) => Promise<void>;
  loading?: boolean;
}) => {
  const {
    rules,
    nameFilter,
    ruleTypeFilter,
    aggressivityFilter,
    threatTypeFilter,
    onCopyRule,
    onEditRule,
    onDeleteRule,
    loading,
  } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<ColumnSort[]>([
    { id: 'raiRuleBuiltIn', desc: true },
    { id: 'raiRuleName', desc: false },
  ]);

  const [preDefinedRuleToView, setPreDefinedRuleToView] =
    useState<CorrelatedRuleShallow | null>(null);
  const [customRuleToView, setCustomRuleToView] =
    useState<CorrelatedRuleShallow | null>(null);
  const [customRuleToDelete, setCustomRuleToDelete] =
    useState<CorrelatedRuleShallow | null>(null);
  const [ruleToCopy, setRuleToCopy] = useState<CorrelatedRuleShallow | null>(
    null
  );

  const handleCopyRule = useCallback(
    async (ruleToCopy: CorrelatedRuleShallow) => {
      setRuleToCopy({
        ...ruleToCopy,
        raiRuleName: `${ruleToCopy.raiRuleName} (${t(
          'common.action.label.copy'
        )})`,
        raiRuleBuiltIn: RuleType.UserDefined,
      });
    },
    [t]
  );

  const columnFilters = useMemo(
    () => [
      { id: 'raiRuleName', value: nameFilter },
      { id: 'raiRuleBuiltIn', value: ruleTypeFilter },
      { id: 'raiAggressiveLevel', value: aggressivityFilter },
      { id: 'raiThreatType', value: threatTypeFilter },
    ],
    [nameFilter, ruleTypeFilter, aggressivityFilter, threatTypeFilter]
  );

  const columns = useMemo(() => {
    const colHelper = createColumnHelper<CorrelatedRuleShallow>();
    return [
      colHelper.display({
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
        meta: {
          customColumnStyle: {
            px: '4x',
          },
        },
      }),
      colHelper.accessor('raiRuleName', {
        header: () => (
          <TextEllipsis popperProps={{ wordBreak: 'break-word' }}>
            {t('globalSetting.correlatedRules.table.ruleName')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <LinkButton
            overflow="hidden"
            onClick={() => {
              if (cell.row.original.raiRuleBuiltIn === RuleType.PreDefined) {
                setPreDefinedRuleToView(cell.row.original);
              } else {
                setCustomRuleToView(cell.row.original);
              }
            }}
          >
            <TextEllipsis>{cell.getValue()}</TextEllipsis>
          </LinkButton>
        ),
        filterFn: (row, columnId, value, addMeta) => {
          if (value === '') return true;
          return filterFns.includesString(row, columnId, value, addMeta);
        },
        minSize: 230,
      }),
      colHelper.accessor('raiAggressiveLevel', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedRules.table.aggressivityLevel')}
          </TextEllipsis>
        ),
        filterFn: (row, _, value: AggressivityFilter) => {
          if (value === AllAggressivity) return true;
          return (
            row.original.raiAggressiveLevel === value &&
            row.original.raiRuleBuiltIn === RuleType.PreDefined
          );
        },
        cell: (cell) => (
          <TextEllipsis>
            {cell.row.original.raiRuleBuiltIn === RuleType.PreDefined
              ? t(aggressivityFilterI18nKeyMap[cell.getValue()])
              : '-'}
          </TextEllipsis>
        ),
        minSize: 160,
      }),
      colHelper.accessor('raiThreatType', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedRules.table.threatType')}
          </TextEllipsis>
        ),
        filterFn: (row, _, value: ThreatTypeFilter) => {
          if (value === 'All') return true;
          return row.original.raiThreatType === value;
        },
        cell: (cell) => <TextEllipsis>{cell.getValue() || '-'}</TextEllipsis>,
        minSize: 180,
      }),
      colHelper.accessor('raiRuleDesc', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedRules.table.description')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <TextEllipsis popperProps={{ wordBreak: 'break-word' }}>
            {cell.getValue()}
          </TextEllipsis>
        ),
        minSize: 185,
      }),
      colHelper.accessor('raiRuleBuiltIn', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedRules.table.ruleType')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <TextEllipsis>
            {cell.getValue() === RuleType.UserDefined
              ? t('globalSetting.correlatedRules.userDefined')
              : t('globalSetting.correlatedRules.preDefined')}
          </TextEllipsis>
        ),
        filterFn: (row, _, value) => {
          if (value === RuleTypeFilter.All) return true;
          else if (value === RuleTypeFilter.PreDefined) {
            return row.original.raiRuleBuiltIn === 1;
          }
          return row.original.raiRuleBuiltIn === 0;
        },
        sortingFn: (a, b) => {
          if (a.original.raiRuleBuiltIn < b.original.raiRuleBuiltIn) return 1;
          if (a.original.raiRuleBuiltIn > b.original.raiRuleBuiltIn) return -1;
          return 0;
        },
        minSize: 125,
      }),
      colHelper.accessor('raiExpressionList', {
        header: () => (
          <TextEllipsis>
            {t('globalSetting.correlatedRules.table.detectionSignals')}
          </TextEllipsis>
        ),
        cell: (cell) => (
          <TagListOverflow
            tags={cell.getValue().map(({ raiExpName }) => raiExpName)}
          />
        ),
        minSize: 260,
        meta: {
          customColumnStyle: {
            flex: 'auto',
            overflow: 'hidden',
          },
        },
      }),
      colHelper.display({
        id: 'actions',
        header: () => null,
        cell: (cell) => {
          const builtIn = cell.row.original.raiRuleBuiltIn;
          const isEmailLoop = cell.row.original.raiEmailLoopEnabled;
          return (
            <Stack gap="2x" flexDirection="row">
              <IconButton
                tooltipLabel={t('common.action.label.copy')}
                onClick={() => handleCopyRule(cell.row.original)}
                disabled={isEmailLoop}
              >
                <FileCopyOIcon />
              </IconButton>
              {!builtIn && !isEmailLoop && (
                <IconButton
                  tooltipLabel={t('common.action.label.delete')}
                  onClick={() => setCustomRuleToDelete(cell.row.original)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          );
        },
        size: 90,
      }),
    ];
  }, [handleCopyRule, t]);

  const table = useReactTable({
    data: rules || [],
    columns,
    defaultColumn: {
      minSize: 48,
    },
    state: {
      rowSelection,
      columnFilters,
      sorting,
    },
    enableRowSelection: true,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      if (typeof updater === 'function') {
        setSorting(updater(sorting));
      }
    },
    getRowId: (originalRow) => {
      return originalRow.raiRuleId;
    },
  });

  return (
    <>
      <Scrollbar
        flex="auto"
        width="100%"
        height="100%"
        overflowX="auto"
        overflowY="hidden"
      >
        <Table layout="flexbox" minWidth="100%" height="100%">
          <TableHeader position="sticky" top={0} zIndex={1}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow width="100%" key={`header-group-${headerGroup.id}`}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={`header-${header.id}`}
                    width={header.getSize()}
                    minWidth={header.column.columnDef.minSize}
                    {...header.column.columnDef.meta?.customColumnStyle}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {loading && (
            <Flex flex={1} alignItems="center" justifyContent="center">
              <Spinner />
            </Flex>
          )}
          {!loading && (
            <Scrollbar
              display="flex"
              flexDirection="column"
              flex="auto"
              overflowY="auto"
              height="100%"
            >
              <TableBody height="100%" width="100%">
                {table.getRowModel().rows.length === 0 && !loading && (
                  <Flex
                    width="100%"
                    height={ROW_HEIGHT * 2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Text color={colorStyle.color.secondary}>
                      {t('common.noData')}
                    </Text>
                  </Flex>
                )}
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    width="100%"
                    height={ROW_HEIGHT}
                    key={`row-${row.id}`}
                    data-selected={dataAttr(row.getIsSelected())}
                    _hover={{
                      backgroundColor: colorStyle.background.selected,
                    }}
                    _selected={{
                      backgroundColor: colorStyle.background.selected,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={`cell-${cell.id}`}
                        display="flex"
                        alignItems="center"
                        width={cell.column.getSize()}
                        minWidth={cell.column.columnDef.minSize}
                        {...cell.column.columnDef.meta?.customColumnStyle}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Scrollbar>
          )}
        </Table>
      </Scrollbar>
      {preDefinedRuleToView && (
        <PreDefinedRuleModal
          rule={preDefinedRuleToView}
          isOpen={true}
          onClose={() => setPreDefinedRuleToView(null)}
        />
      )}
      {customRuleToView && (
        <CustomRuleModal
          defaultRule={customRuleToView}
          isOpen={!!customRuleToView}
          onSave={async (rule) => {
            try {
              await onEditRule(rule);
              setCustomRuleToView(null);
            } catch (e) {
              // User has to modify the rule name or retry
              console.error(e);
            }
          }}
          onClose={() => {
            setCustomRuleToView(null);
          }}
        />
      )}
      {ruleToCopy && (
        <CustomRuleModal
          defaultRule={ruleToCopy}
          isOpen={!!ruleToCopy}
          onSave={async (rule) => {
            try {
              await onCopyRule({ ...rule, raiRuleId: '' });
              setRuleToCopy(null);
            } catch (e) {
              // User has to modify the rule name or retry
              console.error(e);
            }
          }}
          onClose={() => {
            setRuleToCopy(null);
          }}
        />
      )}
      {customRuleToDelete && (
        <DeleteRuleModal
          rule={customRuleToDelete}
          isOpen={!!customRuleToDelete}
          onConfirm={async () => {
            await onDeleteRule(customRuleToDelete.raiRuleId);
            setCustomRuleToDelete(null);
          }}
          onClose={() => setCustomRuleToDelete(null)}
        />
      )}
    </>
  );
};

export default CorrelatedRulesTable;
